// frontend/src/components/quiz/QuestionCard.js
import React, { useState } from "react";
import { CheckCircle, XCircle, AlertCircle, ExternalLink } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { getQuizTranslation } from "../../utils/quizTranslations";

const QuestionCard = ({
  question,
  selectedAnswers = [],
  onAnswerSelect,
  showFeedback = false,
  feedback = null,
  disabled = false,
}) => {
  const { language, t } = useLanguage();
  const [imageError, setImageError] = useState(false);

  const isMultipleSelect = question.question_type === "multiple_select";
  const isTrueFalse = question.question_type === "true_false";

  const handleAnswerClick = (answerId) => {
    if (disabled) return;

    if (isMultipleSelect) {
      // Toggle answer in array
      if (selectedAnswers.includes(answerId)) {
        onAnswerSelect(selectedAnswers.filter((id) => id !== answerId));
      } else {
        onAnswerSelect([...selectedAnswers, answerId]);
      }
    } else {
      // Single selection
      onAnswerSelect([answerId]);
    }
  };

  const isAnswerSelected = (answerId) => selectedAnswers.includes(answerId);

  const getAnswerClassName = (answerId) => {
    const isSelected = isAnswerSelected(answerId);
    const baseClasses =
      "w-full p-4 rounded-lg border-2 transition-all duration-200 text-left flex items-center";

    if (showFeedback && feedback) {
      // Show correct/incorrect styling using corporate colors
      const isCorrect = feedback.correct_answer_ids?.includes(answerId);

      if (isCorrect) {
        // Green for correct: #84BD00
        return `${baseClasses} border-[#84BD00] bg-[#84BD00] bg-opacity-10`;
      } else if (isSelected && !isCorrect) {
        // Red for wrong answer: #DA291C
        return `${baseClasses} border-[#DA291C] bg-[#DA291C] bg-opacity-10`;
      } else {
        return `${baseClasses} border-gray-200 bg-gray-50 opacity-60`;
      }
    }

    if (isSelected) {
      // Primary blue when selected: #00A3E0
      return `${baseClasses} border-[#00A3E0] bg-[#00A3E0] bg-opacity-10`;
    }

    if (disabled) {
      return `${baseClasses} border-gray-200 opacity-60 cursor-not-allowed`;
    }

    return `${baseClasses} border-gray-300 hover:border-[#00A3E0] hover:bg-[#00A3E0] hover:bg-opacity-5 cursor-pointer`;
  };

  const getAnswerIcon = (answerId) => {
    if (!showFeedback || !feedback) {
      // Show checkbox/radio based on question type - using corporate blue #00A3E0
      return (
        <div
          className={`w-5 h-5 rounded-${
            isMultipleSelect ? "sm" : "full"
          } border-2 mr-3 flex-shrink-0 ${
            isAnswerSelected(answerId)
              ? "border-[#00A3E0] bg-[#00A3E0]"
              : "border-gray-400"
          }`}
        >
          {isAnswerSelected(answerId) && (
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full"></div>
            </div>
          )}
        </div>
      );
    }

    // Show feedback icons with corporate colors
    const isCorrect = feedback.correct_answer_ids?.includes(answerId);
    const isSelected = isAnswerSelected(answerId);

    if (isCorrect) {
      // Green #84BD00
      return <CheckCircle className="w-5 h-5 text-[#84BD00] mr-3 flex-shrink-0" />;
    } else if (isSelected && !isCorrect) {
      // Red #DA291C
      return <XCircle className="w-5 h-5 text-[#DA291C] mr-3 flex-shrink-0" />;
    }

    return null;
  };

  return (
    <div className="space-y-6">
      {/* Question Text */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-[#002855]">
          {t(question.question_text)}
        </h2>

        {/* Optional hint/description */}
        {question.hint && (
          <p className="text-sm text-gray-600 italic">
            {t(question.hint)}
          </p>
        )}

        {/* Question Image (if exists) */}
        {question.image_url && !imageError && (
          <div className="relative rounded-lg overflow-hidden bg-gray-100">
            <img
              src={question.image_url}
              alt={question.image_alt_text ? t(question.image_alt_text) : "Question image"}
              className="w-full max-h-96 object-contain"
              onError={() => setImageError(true)}
            />
          </div>
        )}

        {/* Instruction text */}
        <p className="text-sm text-gray-600">
          {isMultipleSelect
            ? getQuizTranslation("ui.selectAnswers", language)
            : getQuizTranslation("ui.selectAnswer", language)}
        </p>
      </div>

      {/* Answers */}
      <div className="space-y-3">
        {question.answers.map((answer) => (
          <button
            key={answer.id}
            type="button"
            onClick={() => handleAnswerClick(answer.id)}
            disabled={disabled}
            className={getAnswerClassName(answer.id)}
          >
            {getAnswerIcon(answer.id)}
            <span className="flex-1">{t(answer.answer_text)}</span>
          </button>
        ))}
      </div>

      {/* Feedback Section - Corporate Colors */}
      {showFeedback && feedback && (
        <div
          className={`p-4 rounded-lg border-2 ${
            feedback.is_correct
              ? "bg-[#84BD00] bg-opacity-10 border-[#84BD00]"
              : "bg-[#DA291C] bg-opacity-10 border-[#DA291C]"
          }`}
        >
          <div className="flex items-start gap-3">
            {feedback.is_correct ? (
              <CheckCircle className="w-6 h-6 text-[#84BD00] flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-6 h-6 text-[#DA291C] flex-shrink-0 mt-0.5" />
            )}

            <div className="flex-1">
              <h3
                className={`font-semibold mb-2 ${
                  feedback.is_correct ? "text-[#002855]" : "text-[#002855]"
                }`}
              >
                {feedback.is_correct
                  ? getQuizTranslation("ui.correct", language)
                  : getQuizTranslation("ui.incorrect", language)}
              </h3>

              {feedback.explanation && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    {getQuizTranslation("ui.explanation", language)}:
                  </p>
                  <p className="text-sm text-gray-600">
                    {t(feedback.explanation)}
                  </p>
                </div>
              )}

              {feedback.learn_more_url && (
                <a
                  href={feedback.learn_more_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-3 text-sm text-[#00A3E0] hover:text-[#EE005F] font-medium transition-colors"
                >
                  {getQuizTranslation("ui.learnMore", language)}
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionCard;
